import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ArrowRight, Users, Briefcase } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function TargetAudiences() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="targetaudiences" ref={sectionRef} className="bg-background overflow-hidden relative ![padding:150px_0px_250px_0px]">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[10%] right-[5%] w-96 h-96 bg-muted/30 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-[10%] left-[5%] w-64 h-64 bg-ring/5 rounded-full blur-3xl opacity-50" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
      <div className="text-center mb-16 lg:mb-20">
  <h2 className="text-4xl md:text-5xl font-bold leading-tight text-[hsl(218,98%,26%)] font-sans">Who we work with</h2>
      </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {}
          <div className={cn("relative group transition-all duration-1000 ease-out transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
            {}
            <div className="relative w-full aspect-[4/3] lg:w-[90%] rounded-lg overflow-hidden shadow-lg ml-auto lg:ml-0 lg:mr-auto">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553131/img/family_camel_ride_negev.png" alt="Family enjoying a camel ride in the Negev desert" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-primary/10 mix-blend-multiply" />
            </div>

            {}
            <Card className="flex flex-col gap-6 py-6 relative bg-primary text-primary-foreground border-none shadow-2xl rounded-lg overflow-hidden z-20 ![margin:-114.951px_0px_-114.951px_0px] !w-[520px] lg:absolute lg:bottom-[-10%] lg:right-[-5%]">
              <CardContent className="p-8 lg:p-10 flex flex-col gap-6">
                <div className="flex items-center gap-3 text-ring">
                  <Users className="w-6 h-6" />
                  <span className="text-sm font-medium tracking-wider uppercase opacity-90">
                    Private Tours
                  </span>
                </div>
                
                <div>
                  <h2 className="font-serif text-3xl lg:text-4xl font-bold mb-2 leading-tight">
                    Families & Groups
                  </h2>
                  <p className="font-sans italic text-primary-foreground/80 text-lg">
                    Creating bonds that last a lifetime
                  </p>
                </div>

                <p className="font-sans text-primary-foreground/90 leading-relaxed">
                  Experience the magic of Israel together. Our tailored itineraries ensure every family member, from toddlers to grandparents, finds joy and connection in the Holy Land through immersive activities and seamless logistics.
                </p>

                <div className="pt-2">
                  <Button asChild variant="secondary" className="bg-white text-primary hover:bg-white/90 border-none shadow-md w-full sm:w-auto">
                    <Link to="/contact">
                      Plan Your Family Journey
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="lg:hidden w-full flex justify-center py-4">
            <Separator className="w-24 bg-border h-[2px]" />
          </div>

          {}
          <div className={cn("relative flex flex-col gap-8 transition-all duration-1000 ease-out delay-200 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
            {}
            <div className="relative w-full aspect-[4/3] lg:w-[90%] lg:ml-auto rounded-lg overflow-hidden shadow-lg order-1 lg:order-2">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553131/img/corporate_meeting_tel_aviv_rooftop.png" alt="Corporate delegation meeting on a Tel Aviv rooftop" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent" />
            </div>

            {}
            <div className="relative z-20 bg-background/95 backdrop-blur-sm lg:bg-transparent lg:backdrop-blur-none p-4 lg:p-0 order-2 lg:order-1 lg:absolute lg:top-[10%] lg:left-[-10%] lg:w-[90%] lg:max-w-lg">
              <div className="bg-background rounded-lg border border-border/50 ![padding:60px_40px_50px_40px] ![margin:-114.951px_0px_-114.951px_0px] lg:shadow-xl">
                <div className="flex items-center gap-3 text-ring mb-6">
                  <Briefcase className="w-6 h-6" />
                  <span className="text-sm font-medium tracking-wider uppercase">
                    Corporate Services
                  </span>
                </div>

                <h2 className="font-serif text-3xl lg:text-4xl font-bold text-primary mb-3 leading-tight">
                  Business & <span className="text-ring">Corporate</span> Delegations
                </h2>
                
                <p className="font-sans italic text-muted-foreground text-lg mb-6 border-l-2 border-ring pl-4">
                  Professional excellence meets cultural immersion
                </p>

                <p className="font-sans text-foreground/80 leading-relaxed mb-8">
                  Elevate your corporate retreat or delegation with seamless logistics, exclusive access to industry leaders, and unforgettable cultural experiences. We handle the details so you can focus on the business at hand.
                </p>

                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg w-full sm:w-auto">
                  <Link to="/contact">
                    Explore Corporate Solutions
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}